import { css } from 'lit';

export const styles = css`
	:host {
		--_sds-switch-background-color: var(--sds-switch-background-color, var(--sds-color-surface-default, var(--sds-color-neutral-surface-default)));
		--_sds-switch-text-color: var(--sds-switch-text-color, var(--sds-color-text-default, var(--sds-color-neutral-text-default)));

		--_sds-switch-transition-time: var(--sds-switch-transition-time, 200ms);

		--_sds-switch-border-color: var(--sds-switch-border-color, var(--sds-color-border-default, var(--sds-color-neutral-border-default)));
		--_sds-switch-border-width: var(--sds-switch-border-width, var(--sds-border-md));
		--_sds-switch-border-radius: var(--sds-switch-border-radius, var(--sds-radius-full));

		--_sds-switch-font-family: var(--sds-switch-font-family, var(--sds-typography-font-family-roboto));
		--_sds-switch-font-size: var(--sds-switch-font-size, var(--sds-components-font-size-content-md));
		--_sds-switch-font-weight: var(--sds-switch-font-weight, var(--sds-typography-font-weight-regular));
		--_sds-switch-line-height: var(--sds-switch-line-height, var(--sds-components-line-height-tall-content-md));

		--_sds-switch-gap: var(--sds-switch-gap, var(--sds-spacing-3xs));
		--_sds-switch-icon-gap: var(--sds-switch-icon-gap, var(--sds-spacing-4xs));
		--_sds-switch-width: var(--sds-switch-width, var(--sds-components-switch-length-md));
		--_sds-switch-height: var(--sds-switch-height, var(--sds-components-icon-md));

		--_sds-switch-button-color: var(--sds-switch-button-color, var(--sds-color-neutral-text-default));
		--_sds-switch-button-size: var(--sds-switch-button-size, var(--sds-components-switch-bulleye-md));
		--_sds-switch-button-gap: var(--sds-switch-button-gap, var(--sds-spacing-4xs));
		--_sds-switch-button-position: var(--sds-switch-button-position, calc(var(--_sds-switch-button-gap) - var(--_sds-switch-border-width)));
		--_sds-switch-button-radius: var(--sds-switch-button-radius, var(--sds-radius-full));

		--_sds-switch-focus-outline-width: var(--sds-switch-focus-outline-width, var(--sds-border-md));
		--_sds-switch-focus-outline-color: var(--sds-switch-focus-outline-color, var(--sds-color-states-focus-outer));
		--_sds-switch-focus-outline-offset: var(--sds-switch-focus-outline-offset, var(--sds-color-states-focus-spacing));
		--_sds-switch-focus-inner-color: var(--sds-switch-focus-inner-color, var(--sds-color-states-focus-inner));

		--_sds-switch-disabled-opacity: var(--sds-switch-disabled-opacity, var(--sds-color-states-disabled-opacity));

		box-sizing: border-box;
	}

	:host([hidden]) {
		display: none;
	}

	:host([readonly]) {
		--_sds-switch-background-color: var(--sds-switch-background-color, var(--sds-color-neutral-surface-tinted));
		--_sds-switch-button-color: var(--sds-switch-button-color, var(--sds-color-neutral-text-default));
		--_sds-switch-border-color: var(--sds-switch-border-color, var(--sds-color-neutral-border-subtle));
		cursor: default;
		pointer-events: none;
	}

	:host([disabled]) {
		opacity: var(--_sds-switch-disabled-opacity);
		pointer-events: none;
	}

	:host([size='s']),
	:host([size='sm']) {
		--_sds-switch-width: var(--sds-switch-width, var(--sds-components-switch-length-sm));
		--_sds-switch-height: var(--sds-switch-height, var(--sds-components-icon-sm));
		--_sds-switch-button-size: var(--sds-switch-button-size, var(--sds-components-switch-bulleye-sm));
		--_sds-switch-font-size: var(--sds-switch-font-size, var(--sds-components-font-size-content-sm));
		--_sds-switch-line-height: var(--sds-switch-line-height, var(--sds-components-line-height-tall-content-sm));
	}

	:host([size='l']),
	:host([size='lg']) {
		--_sds-switch-width: var(--sds-switch-width, var(--sds-components-switch-length-lg));
		--_sds-switch-height: var(--sds-switch-height, var(--sds-components-icon-lg));
		--_sds-switch-button-size: var(--sds-switch-button-size, var(--sds-components-switch-bulleye-lg));
		--_sds-switch-font-size: var(--sds-switch-font-size, var(--sds-components-font-size-content-lg));
		--_sds-switch-line-height: var(--sds-switch-line-height, var(--sds-components-line-height-tall-content-lg));
	}

	.sds-switch {
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: var(--_sds-switch-gap);
		font-family: var(--_sds-switch-font-family);
	}

	.sds-switch__label {
		display: flex;
		align-items: center;
		cursor: pointer;
		user-select: none;
		gap: var(--_sds-switch-icon-gap);
		color: var(--_sds-switch-text-color);
		font-size: var(--_sds-switch-font-size);
		font-weight: var(--_sds-switch-font-weight);
		line-height: var(--_sds-switch-line-height);
	}

	.sds-switch__label--hidden:not(:focus):not(:active):not(:focus-within) {
		clip-path: inset(50%);
		height: 1px;
		overflow: hidden;
		position: absolute;
		white-space: nowrap;
		width: 1px;
	}

	.sds-switch__input {
		appearance: none;
		position: relative;
		cursor: pointer;
		user-select: none;
		margin: 0;

		width: var(--_sds-switch-width);
		height: var(--_sds-switch-height);

		background-color: var(--_sds-switch-background-color);
		border: var(--_sds-switch-border-width) solid var(--_sds-switch-border-color);
		border-radius: var(--_sds-switch-border-radius);

		transition: background-color var(--_sds-switch-transition-time) ease;
	}

	.sds-switch__input::before {
		content: '';
		position: absolute;
		top: 50%;
		transform: translateY(-50%) translateX(var(--_sds-switch-button-position));

		width: var(--_sds-switch-button-size);
		height: var(--_sds-switch-button-size);

		background-color: var(--_sds-switch-button-color);
		border-radius: var(--_sds-switch-button-radius);

		transition:
			transform var(--_sds-switch-transition-time) ease,
			background-color var(--_sds-switch-transition-time) ease;
	}

	.sds-switch__input:focus-visible {
		box-shadow: 0 0 0 var(--sds-color-states-focus-spacing) var(--_sds-switch-focus-inner-color);
		outline: var(--_sds-switch-focus-outline-width) solid var(--_sds-switch-focus-outline-color);
		outline-offset: var(--_sds-switch-focus-outline-offset);
	}

	.sds-switch__input:checked:not([readonly]) {
		--_sds-switch-border-color: var(--sds-switch-border-color, var(--sds-color-main-action-default));
		--_sds-switch-background-color: var(--sds-switch-background-color-checked, var(--sds-color-main-action-default));
		--_sds-switch-button-color: var(--sds-switch-button-color, var(--sds-color-neutral-surface-default));
	}

	.sds-switch__input:checked::before {
		--_sds-switch-button-position: calc(var(--_sds-switch-width) - var(--_sds-switch-button-size) - var(--_sds-switch-button-gap) - var(--_sds-switch-border-width));
	}
`;
